/**
 * MapBackdrop — the Terrain plane (MERIDIAN plane 1).
 *
 * A fixed, full-viewport layer BEHIND all chrome and content. It renders the
 * frozen Search-map snapshot — blurred, saturated, dimmed, and covered by a
 * luminance-adaptive scrim — so the glass bezel reads as floating above the
 * world. It is fully STATIC while the user works: no parallax, no idle motion.
 *
 * It moves during exactly TWO of MERIDIAN's reserved moments (see § Motion):
 *   • SET   — leaving Search: the live map "freezes" into the backdrop. The
 *             terrain animates from a sharp/bright look to its blurred, dimmed
 *             resting state as the new page's moat reveals it.
 *   • THAW  — entering Search: the frozen terrain briefly "wakes" (sharpens +
 *             brightens) in the beat before the live interactive map covers it.
 * Both fire only when CROSSING the Search boundary — navigating between two
 * non-Search pages (or between two Search methods) replays nothing, so the
 * terrain stays still exactly as the design requires.
 *
 * The layer is purely decorative: aria-hidden, not focusable, pointer-events
 * none — the map is only interactive on the Search page itself. When no snapshot
 * has been captured yet, `snapshot` is null and this renders a calm neutral
 * field (no freeze/thaw keyframes) — never a broken or empty state.
 */

import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMapSnapshot } from '../../providers/MapSnapshotProvider';
import './MapBackdrop.css';

type Phase = 'idle' | 'setting' | 'thawing';

export const MapBackdrop = () => {
  const { snapshot } = useMapSnapshot();
  const location = useLocation();
  const isSearch = location.pathname.startsWith('/search');

  // Track the previous side of the Search boundary to fire Set/Thaw only on a
  // crossing (initial mount seeds prev === current, so nothing plays on load).
  const prevIsSearch = useRef(isSearch);
  const [phase, setPhase] = useState<Phase>('idle');

  useEffect(() => {
    const was = prevIsSearch.current;
    if (was && !isSearch) setPhase('setting');   // left Search  → freeze
    else if (!was && isSearch) setPhase('thawing'); // entered Search → wake
    prevIsSearch.current = isSearch;
  }, [isSearch]);

  return (
    <div className="map-backdrop" aria-hidden="true">
      {/* Frozen map frame (or a calm neutral field when unsampled). The one-shot
          Set/Thaw keyframes carry it between its live and frozen looks; on
          animation end we drop back to 'idle' so the base resting state applies
          and the next crossing can replay. */}
      <div
        className={`map-backdrop__terrain${snapshot ? ' has-snapshot' : ''}`}
        data-phase={phase === 'idle' ? undefined : phase}
        style={snapshot ? { backgroundImage: `url(${snapshot})` } : undefined}
        onAnimationEnd={() => setPhase('idle')}
      />
      {/* Luminance-adaptive scrim: guarantees chrome-text contrast regardless of
          what was frozen. Driven by --terrain-scrim (transitions.css). */}
      <div className="map-backdrop__scrim" />
    </div>
  );
};
